import React, { useEffect, useState, useMemo } from "react";
import { 
  Download, 
  RefreshCcw, 
  Calendar, 
  Search, 
  Filter, 
  ChevronLeft, 
  ChevronRight,
  DollarSign,
  Award,
  User,
  Package
} from "lucide-react";
import toast from "react-hot-toast";
import { saveAs } from "file-saver";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const ITEMS_PER_PAGE = 10;

function ManageSales() {
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [distributors, setDistributors] = useState([]);
  
  // Filter States
  const [filterType, setFilterType] = useState("all");
  const [filterValue, setFilterValue] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ Fetch all sales
  const fetchSales = async () => {
    try {
      const res = await fetch(`${SERVER_URL}/api/sales`);
      const data = await res.json();
      if (res.ok) {
        setSales(data);
        setFilteredSales(data);
        toast.success("Sales refreshed!");
      } else {
        toast.error(data.error || "Failed to fetch sales");
      }
    } catch (err) {
      toast.error("Server error while fetching sales.");
    }
  };

  // ✅ Fetch distributors for dropdown
  const fetchDistributors = async () => {
    try {
      const res = await fetch(`${SERVER_URL}/api/distributors`);
      const data = await res.json();
      if (res.ok) setDistributors(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchSales();
    fetchDistributors();
  }, []);

  // ✅ Filtering logic (includes date range)
  useEffect(() => {
    let results = [...sales];

    // Filter by distributor or product
    if (filterType === "distributor" && filterValue) {
      results = results.filter(
        (s) => s.distributor?.name?.toLowerCase() === filterValue.toLowerCase()
      );
    } else if (filterType === "product" && filterValue) {
      results = results.filter(
        (s) => s.product?.toLowerCase().includes(filterValue.toLowerCase()) // Changed to includes for better UX
      );
    }

    // Filter by date range
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      // Set to end of day to include sales made on the "to" date
      to.setHours(23, 59, 59, 999);

      if (from > to) {
        // toast.error("Start date cannot be later than end date."); // Optional: prevent toast spam on typing
      } else {
        results = results.filter((s) => {
          const saleDate = new Date(s.createdAt);
          return saleDate >= from && saleDate <= to;
        });
      }
    }

    setFilteredSales(results);
    setCurrentPage(1); // Reset to page 1 on filter change
  }, [filterType, filterValue, fromDate, toDate, sales]);

  // ✅ Calculate Pagination
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItems = filteredSales.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSales.length / ITEMS_PER_PAGE);

  // ✅ Calculate totals (Based on FILTERED list, not just current page)
  const totalBV = useMemo(
    () => filteredSales.reduce((sum, s) => sum + (s.totalBV || 0), 0),
    [filteredSales]
  );

  const totalP = useMemo(
    () => filteredSales.reduce((sum, s) => sum + (s.totalPrice || 0), 0),
    [filteredSales]
  );

  // ✅ Export as CSV
  const handleExportCSV = () => {
    if (!filteredSales.length) return toast.error("No data to export.");

    const headers = ["Distributor", "Product", "Quantity", "Total Price", "Total BV", "Date"];
    const rows = filteredSales.map((s) => [
      s.distributor?.name || "N/A",
      s.product,
      s.quantity,
      s.totalPrice,
      s.bv, // Note: your logic used 's.bv' in export but displayed 's.totalBV'. Ensure consistency.
      new Date(s.createdAt).toLocaleString(),
    ]);

    // Add total summary at the end
    rows.push([]);
    rows.push(["", "", "TOTALS:", totalP, totalBV, ""]);

    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "sales_report.csv");
    toast.success("Sales exported as CSV!");
  };

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                Sales Records
                <button 
                    onClick={fetchSales} 
                    className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded-full transition-all text-gray-500 hover:text-green-600"
                    title="Refresh Data"
                >
                    <RefreshCcw size={16} />
                </button>
            </h1>
            <p className="text-sm text-gray-500 mt-1">View and filter transaction history.</p>
        </div>
        <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-xl font-medium shadow-lg shadow-green-900/10 transition-all active:scale-95 text-sm"
        >
            <Download size={18} />
            Export Report
        </button>
      </div>

      {/* Filters Toolbar */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 flex flex-col lg:flex-row gap-4 items-center">
        
        {/* Filter Type & Value */}
        <div className="flex flex-1 gap-2 w-full">
            <div className="relative min-w-[140px]">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <select
                    className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none appearance-none"
                    value={filterType}
                    onChange={(e) => {
                        setFilterType(e.target.value);
                        setFilterValue("");
                    }}
                >
                    <option value="all">All Records</option>
                    <option value="distributor">Distributor</option>
                    <option value="product">Product</option>
                </select>
            </div>

            <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                {filterType === "distributor" ? (
                    <select
                        className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none"
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                    >
                        <option value="">All Distributors</option>
                        {distributors.map((d) => (
                            <option key={d._id} value={d.name}>{d.name}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        type="text"
                        placeholder={filterType === "product" ? "Search product name..." : "Filter not active"}
                        className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none disabled:bg-gray-50 disabled:text-gray-400"
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                        disabled={filterType === "all"}
                    />
                )}
            </div>
        </div>

        {/* Date Range */}
        <div className="flex items-center gap-2 w-full lg:w-auto bg-gray-50 p-1 rounded-xl border border-gray-200">
            <div className="flex items-center px-3 gap-2">
                <Calendar size={16} className="text-gray-400" />
                <input
                    type="date"
                    className="bg-transparent text-sm text-gray-600 outline-none"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                />
            </div>
            <span className="text-gray-300">|</span>
            <div className="flex items-center px-3 gap-2">
                <input
                    type="date"
                    className="bg-transparent text-sm text-gray-600 outline-none"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                />
            </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 shadow-sm rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50/50">
                <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Distributor</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Price (Total)</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Qty</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">BV (Total)</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
                {currentItems.length === 0 ? (
                    <tr>
                        <td colSpan="6" className="text-center py-12">
                            <p className="text-gray-500 font-medium">No sales found.</p>
                            <p className="text-xs text-gray-400 mt-1">Try adjusting your filters.</p>
                        </td>
                    </tr>
                ) : (
                    currentItems.map((s) => (
                    <tr key={s._id} className="hover:bg-green-50/30 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                                <User size={16} className="text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">{s.distributor?.name || "Unknown"}</span>
                            </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                                <Package size={16} className="text-gray-400" />
                                <span className="text-sm text-gray-600">{s.product}</span>
                            </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-semibold text-gray-900">{s.totalPrice?.toLocaleString()} KES</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {s.quantity}
                            </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-green-700">{s.totalBV}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(s.createdAt).toLocaleDateString()}
                        </td>
                    </tr>
                    ))
                )}
            </tbody>
            </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                    Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, filteredSales.length)}</span> of <span className="font-medium">{filteredSales.length}</span> entries
                </p>
                <div className="flex gap-2">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                        className="p-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                        className="p-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        )}
      </div>

      {/* Footer Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 justify-end">
        <div className="bg-white p-4 rounded-xl border border-green-100 shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg text-green-600">
                    <DollarSign size={24} />
                </div>
                <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Total Revenue</p>
                    <p className="text-sm text-gray-400">Based on filtered selection</p>
                </div>
            </div>
            <p className="text-xl font-bold text-gray-900">{totalP.toLocaleString()} <span className="text-sm font-normal text-gray-500">KES</span></p>
        </div>

        <div className="bg-white p-4 rounded-xl border border-blue-100 shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                    <Award size={24} />
                </div>
                <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Total BV</p>
                    <p className="text-sm text-gray-400">Points Awarded</p>
                </div>
            </div>
            <p className="text-xl font-bold text-gray-900">{totalBV.toLocaleString()}</p>
        </div>
      </div>

    </div>
  );
}

export default ManageSales;